import { auth } from '../firebase';

const ACTIVE_ATTEMPT_STORAGE_KEY = 'valenpos_active_sale_attempt_v2';
const ACTIVE_RETURN_ATTEMPT_STORAGE_KEY = 'valenpos_active_return_attempt_v1';

export class SaleApiError extends Error {
  constructor(message, { code = 'SALE_FAILED', status = 0, uncertain = false } = {}) {
    super(message);
    this.name = 'SaleApiError';
    this.code = code;
    this.status = status;
    this.uncertain = uncertain;
  }
}

const parseApiJson = async (res) => {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (_error) {
    return { error: text || 'Respuesta inválida del servidor' };
  }
};

const fallbackUuid = () => {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createSaleIdempotencyKey = () => {
  const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : fallbackUuid();
  return `sale_${uuid.replace(/-/g, '_')}`;
};

const normalizeForFingerprint = (value) => {
  if (Array.isArray(value)) return value.map(normalizeForFingerprint);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((out, key) => {
      out[key] = normalizeForFingerprint(value[key]);
      return out;
    }, {});
  }
  return value;
};

export const createSaleFingerprint = ({ saleDoc, storeId }) => JSON.stringify(normalizeForFingerprint({
  storeId,
  turnId: saleDoc?.turnId || saleDoc?.cashId || null,
  origin: saleDoc?.origin || 'pos',
  orderRef: saleDoc?.orderRef || null,
  orderInfo: saleDoc?.orderInfo || null,
  notes: saleDoc?.notes || null,
  customerName: saleDoc?.customerName || null,
  customerId: saleDoc?.customerId || null,
  customerCC: saleDoc?.customerCC || null,
  products: (saleDoc?.products || []).map((item) => ({
    id: item?.id,
    quantity: Number(item?.quantity ?? item?.qty ?? 0),
    recipe: (item?.recipe || item?.recipeComponents || []).map((component) => ({
      insumoId: component?.insumoId || component?.productId || component?.id,
      qtyPerUnit: Number(component?.qtyPerUnit ?? component?.qty ?? component?.quantity ?? 0),
      unit: component?.unit || '',
    })),
  })),
  paymentMethod: saleDoc?.paymentMethod || null,
  secondPaymentMethod: saleDoc?.secondPaymentMethod || null,
  amountReceived: Number(saleDoc?.amountReceived || 0),
  secondAmountReceived: Number(saleDoc?.secondAmountReceived || 0),
}));

const readStoredAttempt = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_ATTEMPT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.key && parsed?.fingerprint ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const storeAttempt = (attempt) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(ACTIVE_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt)); } catch (_error) {}
};

export const getOrCreateSaleAttempt = (fingerprint) => {
  const stored = readStoredAttempt();
  if (stored?.fingerprint === fingerprint) return stored;
  const attempt = { key: createSaleIdempotencyKey(), fingerprint, createdAt: Date.now() };
  storeAttempt(attempt);
  return attempt;
};

export const clearSaleAttempt = (key) => {
  if (typeof window === 'undefined') return;
  const stored = readStoredAttempt();
  if (!key || stored?.key === key) {
    try { window.localStorage.removeItem(ACTIVE_ATTEMPT_STORAGE_KEY); } catch (_error) {}
  }
};

export const orderSaleIdempotencyKey = (orderId) => {
  const safe = String(orderId || '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 100);
  if (!safe) return createSaleIdempotencyKey();
  return `sale_order_${safe}`.padEnd(16, '_');
};

export const createReturnFingerprint = ({ saleId, lines, restock, refundMethod, reason, storeId = 'SUC001' }) => (
  JSON.stringify(normalizeForFingerprint({ saleId, lines, restock, refundMethod, reason, storeId }))
);

export const getOrCreateReturnAttempt = (fingerprint) => {
  if (typeof window !== 'undefined') {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(ACTIVE_RETURN_ATTEMPT_STORAGE_KEY) || 'null');
      if (parsed?.key && parsed?.fingerprint === fingerprint) return parsed;
    } catch (_error) {}
  }
  const attempt = {
    key: `return_${(typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : fallbackUuid()).replace(/-/g, '_')}`,
    fingerprint,
    createdAt: Date.now(),
  };
  if (typeof window !== 'undefined') {
    try { window.localStorage.setItem(ACTIVE_RETURN_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt)); } catch (_error) {}
  }
  return attempt;
};

export const clearReturnAttempt = (key) => {
  if (typeof window === 'undefined') return;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ACTIVE_RETURN_ATTEMPT_STORAGE_KEY) || 'null');
    if (!key || parsed?.key === key) window.localStorage.removeItem(ACTIVE_RETURN_ATTEMPT_STORAGE_KEY);
  } catch (_error) {
    try { window.localStorage.removeItem(ACTIVE_RETURN_ATTEMPT_STORAGE_KEY); } catch (_ignored) {}
  }
};

export async function processSaleApi({ saleDoc, storeId = 'SUC001', idempotencyKey }) {
  if (!idempotencyKey) {
    throw new SaleApiError('No se pudo preparar un reintento seguro para la venta.', {
      code: 'SALE_IDEMPOTENCY_REQUIRED',
    });
  }

  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new SaleApiError('No se pudo obtener la sesión. Vuelve a iniciar sesión.', { code: 'AUTH_REQUIRED' });
  }

  let res;
  try {
    res = await fetch('/api/sales/process', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({ saleDoc, storeId }),
    });
  } catch (_error) {
    throw new SaleApiError(
      'No se pudo confirmar si la venta fue registrada. Conserva el carrito y reintenta la misma operación.',
      { code: 'SALE_NETWORK_UNCERTAIN', uncertain: true }
    );
  }

  const data = await parseApiJson(res);
  if (!res.ok) {
    throw new SaleApiError(data?.error || 'No se pudo procesar la venta', {
      code: data?.code || 'SALE_FAILED',
      status: res.status,
      uncertain: data?.code === 'SALE_PROCESSING',
    });
  }

  return data;
}

export async function processSaleReturnApi({
  saleId,
  lines,
  restock = true,
  refundMethod,
  reason,
  storeId = 'SUC001',
  idempotencyKey,
}) {
  if (!idempotencyKey) {
    throw new SaleApiError('No se pudo preparar un reintento seguro para la devolución.', {
      code: 'SALE_IDEMPOTENCY_REQUIRED',
    });
  }
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new SaleApiError('No se pudo obtener la sesión. Vuelve a iniciar sesión.', { code: 'AUTH_REQUIRED' });

  let res;
  try {
    res = await fetch('/api/sales/process', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({
        action: 'return', saleId, lines, restock, refundMethod, reason, storeId,
      }),
    });
  } catch (_error) {
    throw new SaleApiError(
      'No se pudo confirmar si la devolución fue registrada. Reintenta la misma operación.',
      { code: 'SALE_RETURN_NETWORK_UNCERTAIN', uncertain: true }
    );
  }

  const data = await parseApiJson(res);
  if (!res.ok) {
    throw new SaleApiError(data?.error || 'No se pudo procesar la devolución', {
      code: data?.code || 'SALE_RETURN_FAILED',
      status: res.status,
      uncertain: data?.code === 'SALE_PROCESSING',
    });
  }
  return data;
}
